import { Fragment, type ReactElement } from 'react'
import type { ActionQueueDef } from 'app/components/action-center/queueRegistry'
import { ReasonBadge } from 'app/components/action-center/ReasonBadge'
import type { BoardTask } from 'app/components/action-center/useBoardTasks'
import { CommunicationsSection } from 'app/components/pipelines/CommunicationsSection'
import type { CommContext } from 'app/hooks/usePipelineCommContext'
import type { TaskCounts } from 'app/hooks/usePipelineTaskCounts'
import type { ContactJson } from 'app/types/entities'
import type { TreatmentCard } from 'app/types/treatment'
import type { CaseCardSection, ContactCardSection, RecipeAndSubject } from 'lib/cardRecipe'
import type { CardDensity } from 'lib/listCardConfig'
import { type ContactCardFacts, contactCardFacts } from 'app/components/lists/card/contactCardFacts'
import { BottomSection } from 'app/components/lists/card/sections/BottomSection'
import { CaseHeaderSection } from 'app/components/lists/card/sections/CaseHeaderSection'
import { ChipsSection } from 'app/components/lists/card/sections/ChipsSection'
import { CommsLazySection } from 'app/components/lists/card/sections/CommsLazySection'
import { CueSection } from 'app/components/lists/card/sections/CueSection'
import { DetailsSection } from 'app/components/lists/card/sections/DetailsSection'
import { HeroSection } from 'app/components/lists/card/sections/HeroSection'
import { IdentitySection } from 'app/components/lists/card/sections/IdentitySection'
import { MetaSection } from 'app/components/lists/card/sections/MetaSection'
import { NameRowSection } from 'app/components/lists/card/sections/NameRowSection'
import { ProcedureGroupsSection } from 'app/components/lists/card/sections/ProcedureGroupsSection'
import { SplitBarSection } from 'app/components/lists/card/sections/SplitBarSection'
import { TagsSection } from 'app/components/lists/card/sections/TagsSection'

/** What a card's sections read beyond the subject. Each section takes only what it needs. */
export type CardContext = {
  /** The queue a system board card is drawn for. The board sections derive their facts from it. */
  def?: ActionQueueDef
  /** Open tasks already assigned against this contact, if any. */
  tasks?: BoardTask[]
  /** Tag colours are configured per workspace. See `Tag`. */
  workspaceId?: string
  onText?: () => void
  onSnooze?: () => void
  taskCounts?: TaskCounts
  commContext?: CommContext
}

function ContactSection({
  section,
  contact,
  density,
  context,
  derived,
}: {
  section: ContactCardSection
  contact: ContactJson
  density: CardDensity
  context: CardContext
  derived: ContactCardFacts | undefined
}): ReactElement | null {
  switch (section.kind) {
    case `identity`:
      return <IdentitySection section={section} contact={contact} density={density} />
    case `hero`:
      return <HeroSection field={section.field} contact={contact} density={density} />
    case `meta`:
      return <MetaSection fields={section.fields} contact={contact} density={density} taskCounts={context.taskCounts} />
    case `comms`:
      if (section.source === `context`)
        return (
          <CommunicationsSection
            commContext={context.commContext}
            compact={density === `compact`}
            className="rounded-b-lg"
          />
        )
      break
  }
  // The board sections read what the queue says about this contact. Without a queue they have
  // nothing to say yet: what urgency means on a custom list is G7b's question.
  if (!derived) return null
  switch (section.kind) {
    case `nameRow`:
      return <NameRowSection contact={contact} derived={derived} />
    case `cue`:
      return <CueSection derived={derived} />
    case `tags`:
      return <TagsSection derived={derived} workspaceId={context.workspaceId} />
    case `details`:
      return <DetailsSection derived={derived} />
    case `chips`:
      return <ChipsSection derived={derived} />
    case `comms`:
      return <CommsLazySection contact={contact} derived={derived} />
    case `bottom`:
      return (
        <BottomSection
          contact={contact}
          derived={derived}
          variant={section.variant}
          onText={context.onText}
          onSnooze={context.onSnooze}
        />
      )
  }
  // A section kind added to `ContactCardSection` without a case above fails here at compile
  // time instead of silently drawing nothing.
  const unhandled: never = section
  return unhandled
}

function CaseSection({ section, card }: { section: CaseCardSection; card: TreatmentCard }): ReactElement {
  switch (section.kind) {
    case `caseHeader`:
      return <CaseHeaderSection card={card} />
    case `reasonBadge`:
      return (
        <div className="mt-2">
          <ReasonBadge card={card} />
        </div>
      )
    case `procedureGroups`:
      return <ProcedureGroupsSection card={card} />
    case `splitBar`:
      return <SplitBarSection card={card} />
  }
}

/** A recipe's sections in order, without a frame. `RecipeCard` puts them in one. */
export function RecipeSections({
  recipe,
  subject,
  context,
}: RecipeAndSubject & { context: CardContext }): ReactElement | null {
  if (recipe.subject === `contact` && subject.kind === `contact`) {
    const { contact } = subject
    const { density } = recipe
    const derived = context.def ? contactCardFacts(context.def, contact, context.tasks) : undefined
    return (
      <Fragment>
        {recipe.sections.map((section, index) => (
          // By position: nothing makes a recipe's section kinds unique.
          <ContactSection
            key={index}
            section={section}
            contact={contact}
            density={density}
            context={context}
            derived={derived}
          />
        ))}
      </Fragment>
    )
  }
  if (recipe.subject === `case` && subject.kind === `case`) {
    const { card } = subject
    return (
      <Fragment>
        {recipe.sections.map((section, index) => (
          <CaseSection key={index} section={section} card={card} />
        ))}
      </Fragment>
    )
  }
  return null
}
